import { UseApiCall, apiCall } from "@/common/api/ApiResult.model";
import {
  PlaybookDescriptor,
  PlaybookDataDescriptor,
  PlaybookDataScope,
  PlaybookDataExecutionStatus,
  PlaybookDataRuntimeScope,
} from "./model";
import axios from "axios";
import { DownloadService } from "@/common/download.service";
import { SharedState } from "@/common/state";
import { load_local_playbooks } from "./local_playbooks";
import { merge_by, obj_keep_non_empty_values } from "@/fp";

export const get_playbooks = apiCall<void, PlaybookDescriptor[]>(
  "cloud-playbooks.all",
  // @ts-ignore
  async () => {
    const local_playbook = load_local_playbooks();
    const merge_playbooks_by_id = merge_by<PlaybookDescriptor, "id">("id");
    const res = await axios.get("/cloud-playbooks");
    return {
      success: true,
      value: merge_playbooks_by_id(
        res.data
          .filter(
            // all the rest is junk
            (d: { id: string }) => d.id == "mpip"
          )
          .map((s: any) => {
            return {
              ...s,
              action: s.title,
              description: s.shortDesc,
              type: "cloud",
              display: {
                long_desc: s.longDesc,
                template_title: s.templateTitle,
                pseudo_script: s.pseudoScript,
              },
              cascading_threats: [],
              threats: ["Data Leaks", "Compliance Violation"],
              posture: "data_protection",
            };
          })
      )(local_playbook.data),
    };
  }
);

export const get_playbook_data_instances = UseApiCall<
  {
    playbook_id: string;
    filters: {
      findingSessionId?: string;
      findingTypes?: string[];
      labelIds?: string[];
      serviceId?: string;
      applicationId?: string;
    };
  },
  PlaybookDataDescriptor[]
>("cloud-playbooks.instances", async ({ playbook_id, filters }) => {
  const { findingSessionId, ...criteria } = filters;
  const payload = obj_keep_non_empty_values({
    findingSessionId,
    // optional
    criteria,
  });
  const res = await axios.post(
    `/cloud-playbooks/playbook/${playbook_id}/instances`,
    payload
  );
  return {
    success: true,
    value: res.data.reverse(),
  };
});

export const get_specific_playbook_data_instances = apiCall<
  { playbook_id: string; instances: PlaybookDataDescriptor[] },
  PlaybookDataDescriptor[]
>("cloud-playbooks.specific-instances", async ({ instances, playbook_id }) => {
  const res = await axios.post(
    `/cloud-playbooks/playbook/${playbook_id}/instances/listed`,
    instances.map((d) => d.id)
  );
  return {
    success: true,
    value: res.data,
  };
});

export const create_playbook_data_instance = apiCall<
  { playbook_id: string; scope: PlaybookDataScope },
  PlaybookDataDescriptor
>("cloud-playbooks.create-instance", async ({ playbook_id, scope }) => {
  const res = await axios.post(
    `/cloud-playbooks/playbook/${playbook_id}/session/${scope.sessionId}/instance`,
    scope
  );
  return {
    success: true,
    value: res.data,
  };
});

// stop data collecting phase
export const cancel_playbook_instance_building = apiCall<
  { instance_id: string; playbook_id: string },
  PlaybookDataExecutionStatus
>(
  "cloud-playbooks.stop-instance-building",
  async ({ instance_id, playbook_id }) => {
    const res = await axios.post(
      `/cloud-playbooks/playbook/${playbook_id}/instance/${instance_id}/collect/stop`
    );
    return {
      success: true,
      value: res.data.status,
    };
  }
);

export const get_playbook_instance_status = apiCall<
  { instance_ids: string[]; playbook_id: string },
  Array<{ id: string; status: PlaybookDataExecutionStatus }>
>("cloud-playbooks.instance-status", async ({ instance_ids, playbook_id }) => {
  const res = await axios.post(
    `/cloud-playbooks/playbook/${playbook_id}/statuses`,
    instance_ids
  );
  return {
    success: true,
    value: res.data,
  };
});

export const run_playbook_instance = apiCall<
  { playbook_id: string; instance_id: string },
  PlaybookDataRuntimeScope
>("cloud-playbooks.run-instance", async ({ playbook_id, instance_id }) => {
  const res = await axios.post(
    `/cloud-playbooks/playbook/${playbook_id}/instance/${instance_id}/run`
  );
  return {
    success: true,
    value: res.data,
  };
});

export const stop_playbook_instance_running = apiCall<
  { instance_id: string; playbook_id: string },
  PlaybookDataExecutionStatus
>(
  "cloud-playbooks.cancel-instance-run",
  async ({ instance_id, playbook_id }) => {
    const res = await axios.post(
      `/cloud-playbooks/playbook/${playbook_id}/instance/${instance_id}/stop`
    );
    return {
      success: true,
      value: res.data.status,
    };
  }
);

export const get_playbook_data_url = apiCall<string, string>(
  "cloud-playbooks.get-data-url",
  async (playbook_data_instance_id) => {
    const res = await axios.get(
      `/playbook-data-download/${playbook_data_instance_id}`
    );
    return {
      success: true,
      value: res.data,
    };
  }
);

export const download_data_instance = apiCall<
  { instance: PlaybookDataDescriptor; playbook_name: string },
  boolean
>(
  "cloud-playbooks.download-instance-data",
  async ({ instance, playbook_name }) => {
    // get the public storage url for the data
    const res = await get_playbook_data_url(instance.id);
    if (res.success) {
      const public_storage_url = res.value;
      // download the data from the storage
      const [file_name, download] = DownloadService.from_web.csv({
        authorization: SharedState.loggedUser.value.authorization,
        fileName: `${playbook_name}.csv`,
        filePath: public_storage_url,
      });
      await download();
      // report the server that the download is done so that it can delete the data
      // from the storage
      /*  await axios.post(
        `/report-download-complete/${playbook_data_instance_id}`,
        {
          url: public_storage_url,
        }
      ); */
      return {
        success: true,
        value: true,
      };
    } else {
      return {
        success: true,
        value: false,
      };
    }
  }
  /*   */
);

export const create_custom_data_instance = apiCall<
  string,
  PlaybookDataDescriptor
>("cloud-playbooks.create-custom-instance", async (playbook_id) => {
  const res = await axios.post(`/cloud-playbooks`, {
    playbook_id,
  });
  return {
    success: true,
    value: res.data,
  };
});

export const upload_custom_data_instance = apiCall<
  { file: File; playbook_instance_id: string },
  boolean
>(
  "cloud-playbooks.upload-instance-data",
  async ({ file, playbook_instance_id }) => {
    const res = await axios.get(
      `/get-upload-storage-url/${playbook_instance_id}`
    );
    const upload_url = res.data;
    // upload file to storage
    await axios.post(upload_url, file);
    // report upload done
    await axios.post(`/report-upload-complete`, {
      upload_url,
      playbook_instance_id,
    });
    return {
      success: true,
      value: res.data,
    };
  }
);
